import { forwardRef, type SelectHTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

/**
 * Native select styled to match other inputs. Keeps native a11y (keyboard
 * + screen reader) without a custom listbox.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, invalid, children, ...props }, ref) => (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cn(
        'flex h-10 w-full rounded-md border bg-white px-3 py-2 text-sm text-ink-900 appearance-none',
        'border-ink-200',
        'bg-[length:0.65em_auto] bg-no-repeat bg-[right_0.75rem_center]',
        // small chevron via inline SVG so we don't need extra deps
        'bg-[url(\'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="12" height="8" viewBox="0 0 12 8" fill="none" stroke="%2378716c" stroke-width="2"><path d="M1 1l5 5 5-5"/></svg>\')]',
        'pr-9',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lavender-400 focus-visible:border-lavender-300',
        'disabled:cursor-not-allowed disabled:bg-ink-100 disabled:text-ink-500',
        'transition-colors',
        invalid && 'border-rose-300 focus-visible:ring-rose-300',
        className,
      )}
      {...props}
    >
      {children}
    </select>
  ),
);
Select.displayName = 'Select';
